// ui\client\src\components\Gallery.js
import React, { useState } from "react";
import { fetchSecure } from "../utils/utils";
import { MdDelete } from "react-icons/md";

const Gallery = ({
  images,
  handleSelect,
  canDelete,
  reloadImages,
  ...props
}) => {
  // track items being deleted (by fileName)
  const [deleting, setDeleting] = useState(new Set());
  const isBlog = props?.isBlog || false;
  console.log("PopupGallery :Gallery: isBlog", isBlog);

  const isDeleting = (fileName) => deleting.has(fileName);

  const setDeletingOn = (fileName) => {
    setDeleting((prev) => {
      const next = new Set(prev);
      next.add(fileName);
      return next;
    });
  };
  const setDeletingOff = (fileName) => {
    setDeleting((prev) => {
      const next = new Set(prev);
      next.delete(fileName);
      return next;
    });
  };

  // Prefer explicit type; otherwise infer by extension ignoring query string
  const isVideo = (image) => {
    if (image?.type) return image.type === "video";
    const src = image?.src || "";
    const bare = src.split("?")[0].toLowerCase();
    return (
      bare.endsWith(".mp4") || bare.endsWith(".webm") || bare.endsWith(".ogg")
    );
  };

  // inline SVG spinner (no css classes/keyframes needed)
  const Spinner = () => (
    <svg
      viewBox="0 0 50 50"
      width="36"
      height="36"
      style={{ display: "block" }}
    >
      <circle
        cx="25"
        cy="25"
        r="20"
        fill="none"
        stroke="rgba(255,255,255,0.8)"
        strokeWidth="5"
        strokeLinecap="round"
        strokeDasharray="31.4 31.4"
        transform="rotate(-90 25 25)"
      >
        <animateTransform
          attributeName="transform"
          type="rotate"
          from="-90 25 25"
          to="270 25 25"
          dur="0.8s"
          repeatCount="indefinite"
        />
      </circle>
    </svg>
  );

  const handleDelete = async (fileName) => {
    setDeletingOn(fileName);
    try {
      let response;
      if (isBlog) {
        // fileName is the object name we returned in getBlogFileURLs
        const folder = props?.userDeep?.id; // already available in PopupGallery props
        response = await fetchSecure(
          `/api/deleteBlogMedia/${encodeURIComponent(
            folder
          )}/${encodeURIComponent(fileName)}`,
          {
            method: "DELETE",
          }
        );
      } else {
        response = await fetchSecure("/api/deleteImage/" + fileName);
      }
      const data = await response.json();
      // your API signals success with a "message" prop
      if (data?.hasOwnProperty("message")) {
        // refresh list; PopupGallery's reloadImages is async
        if (reloadImages) await reloadImages();
      }
    } catch (e) {
      // swallow, just stop spinner
      console.error("Delete failed:", e);
    } finally {
      setDeletingOff(fileName);
    }
  };

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))",
        gap: "12px",
        width: "100%",
      }}
    >
      {images.map((image, index) => {
        const objectName = image.name || image.fileName; // added by server
        const deletingThis = isDeleting(objectName);
        return (
          <div
            key={index}
            style={{
              position: "relative",
              borderRadius: 8,
              overflow: "hidden",
              boxShadow: "0 1px 4px rgba(0,0,0,0.12)",
              background: "#fff",
              // Ensure a nice, centered box even before image loads
              minHeight: 160,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
            }}
            onClick={() => {
              if (!deletingThis) {
                handleSelect(index, image.src);
              }
            }}
          >
            {/* Media wrapper – flex centers the content vertically & horizontally */}
            <div
              style={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {isVideo(image) ? (
                <video
                  src={image.src}
                  controls
                  style={{
                    display: "block",
                    width: "100%",
                    height: "auto",
                    maxWidth: "100%",
                    borderRadius: 6,
                    pointerEvents: deletingThis ? "none" : "auto",
                  }}
                />
              ) : (
                <img
                  src={image.src}
                  alt={`Gallery item ${index}`}
                  style={{
                    display: "block",
                    width: "100%",
                    height: "auto",
                    maxWidth: "100%",
                    pointerEvents: deletingThis ? "none" : "auto",
                  }}
                  onClick={() => {
                    if (!deletingThis) {
                      handleSelect(index, image.src);
                    }
                  }}
                />
              )}
            </div>

            {/* Delete button (top-right) */}
            {canDelete && (
              <div
                style={{
                  position: "absolute",
                  top: 6,
                  right: 6,
                  zIndex: 10001,
                  background: "rgba(0,0,0,0.5)",
                  borderRadius: 6,
                  padding: 4,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  opacity: deletingThis ? 0.5 : 1,
                }}
                onClick={(e) => {
                  e.stopPropagation(); // don't select
                  if (!deletingThis) {
                    const label = isVideo(image) ? "video" : "image";
                    if (
                      window.confirm(
                        `Are you sure you wish to delete this ${label}?`
                      )
                    ) {
                      handleDelete(objectName);
                    }
                  }
                }}
                title="Delete"
              >
                <MdDelete
                  size={22}
                  color="#fff"
                  style={{ cursor: "pointer" }}
                />
              </div>
            )}

            {/* Per-item deleting overlay */}
            {deletingThis && (
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  background: "rgba(0,0,0,0.35)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  zIndex: 10000,
                  pointerEvents: "none",
                }}
              >
                <Spinner />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default Gallery;
